import React, { FC, useState } from 'react';
import Document from '@this/impl/api/models/document';
import Structure from '@this/impl/api/models/structure';
import ProgressiveImage from '@this/impl/components/ProgressiveImage';
import FavoriteButton from '@this/impl/components/FavoriteButton';
import * as _ from 'lodash';
import LinesEllipsis from 'react-lines-ellipsis';
import { Link } from 'react-router-dom';
import moment from 'moment-with-locales-es6';
import Checks from '@this/template/components/bootstrap/forms/Checks';
import api from '@this/impl/api';
import Badge from '@this/template/components/bootstrap/Badge';
import Card, {
  CardBody,
  CardHeader,
  CardSubTitle
} from '../../../../template/components/bootstrap/Card';
import useDarkMode from '../../../../template/hooks/useDarkMode';

interface DocumentsProps {
  isFluid?: boolean;
  data: (Document & { relationType?: 'linkedByMe' | 'linkedToMe' })[] | Structure[] | undefined;
  emptyMessage?: string;
  cardsPerRow?: number;
  defaultShowPreview?: boolean;
  showLastAccess?: boolean;
  groupByRelationType?: boolean;
  groupByCategory?: boolean;
}

const DocumentsCards: FC<DocumentsProps> = ({ 
  isFluid, 
  data, 
  emptyMessage, 
  cardsPerRow, 
  defaultShowPreview, 
  showLastAccess, 
  groupByRelationType,
  groupByCategory = true
}) => {
  const { themeStatus, darkModeStatus } = useDarkMode();
  const categoriesQuery = api.Contents.useCategories();
  const [showPreview, setShowPreview] = useState<boolean>(defaultShowPreview ?? false);
  const handleShowPreview = () => setShowPreview(v => !v);

  const relatedDocs = (data as (Document & { relationType?: 'linkedByMe' | 'linkedToMe' })[]) ?? [];
  
  const docs = groupByCategory 
    ? _.groupBy(relatedDocs, (d) => d.categories?.[0] ?? 'uncategorized')
    : { 'all': relatedDocs };

  const renderDocumentCard = (d: Document, idx: number) => (
    <div className='row' key={`${d.id}-${idx}`}>
      <Card stretch shadow='none' className='border rounded mb-3'>
        <CardHeader>
          <CardBody>
            <div className='d-flex justify-content-between align-items-start mb-2'>
              <div className='flex-grow-1'>
                <Link to={`/documents/${d.externalId ?? d.id}`} aria-label={d.name}>
                  <LinesEllipsis
                    style={{ fontSize: '14px' }}
                    text={d.name}
                    maxLine='3'
                    ellipsis='...'
                    trimRight
                    basedOn='letters'
                  />
                </Link>
                <LinesEllipsis
                  style={{ fontSize: '12px' }}
                  text={d.description}
                  maxLine='1'
                  ellipsis='...'
                  trimRight
                  basedOn='letters'
                />
              </div>
              <div className='flex-shrink-0 ms-2'>
                <FavoriteButton
                  documentId={d.id ?? d.externalId ?? ''}
                  isFavorite={d.favorite || false}
                  size='sm'
                />
              </div>
            </div>
            {showLastAccess && (
              <CardSubTitle className='text-muted mt-2'>
                Último acceso: {moment(d.actionDate).format('DD/MM/YYYY HH:mm')}
              </CardSubTitle>
            )}
            {showPreview && (
              <div style={{ backgroundColor: '#e6eef2' }} className='text-center rounded mt-2'>
                <ProgressiveImage
                  width='100%'
                  height={150}
                  src={d.thumbnailLink ?? ''}
                  className='rounded center p-2'
                  ariaLabel={d.thumbnailLink}
                />
              </div>
            )}
          </CardBody>
        </CardHeader>
      </Card>
    </div>
  );

  return (
    <div>
      <Checks
        type='switch'
        id='all'
        label='Vista Previa'
        checked={showPreview}
        onChange={handleShowPreview}
        className='border-bottom border-dark mb-2'
      />
      <div className='mt-2'>
        {Object.entries(docs)
          .sort(([categoryIdA], [categoryIdB]) => {
            // Place 'uncategorized' at the end
            if (categoryIdA === 'uncategorized') return 1;
            if (categoryIdB === 'uncategorized') return -1;
            return 0;
          })
          .map(([categoryId, documents]) => {
            const showCategoryBadge = groupByCategory;
            const category = showCategoryBadge ? categoriesQuery.all.data?.find((cat) => cat.id === categoryId && cat.visible !== false) : null;
            const name = showCategoryBadge ? (category?.name ?? 'Sin categoría') : '';
            
            // Show all documents without separating by relation type
            return (
              <div key={categoryId} className='mb-4'>
                {showCategoryBadge && <Badge>{name}</Badge>}
                <div className='mt-2'>
                  {documents.map(renderDocumentCard)}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

DocumentsCards.defaultProps = {
  isFluid: false,
  emptyMessage: '',
  groupByRelationType: false,
  groupByCategory: true,
};

export default DocumentsCards;
